import { z } from 'zod';

import { localDateTimeSchema } from '@apple/utils/globalization/models';

import { customerGroupSchema } from '@apple/features/customer/management.models';
import { productKeySchema, productModelSchema } from '@apple/features/product/models/product';

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.MutlipleCustomerOrderResponse */
export type StoreCartsResponse = z.infer<typeof storeCartsResponseSchema>;

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.OrderItemQuantityUpdateResponse */
export type StoreCartResponse = z.infer<typeof storeCartResponseSchema>;

/** Manzanita.Web.Models.CustomerOrderModel */
export type StoreCart = z.infer<typeof customerOrderSchema>;

/** Manzanita.Web.Models.CustomerOrderProductModel */
export type StoreCartLine = z.infer<typeof customerProductSchema>;

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.CustomerOrderFilter */
export type StoreCartLineFilters = z.infer<typeof storeCartLineFiltersSchema>;

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.UpdateLineItemQuantityRequest */
export type SetStoreItemQuantityRequest = z.infer<typeof setStoreItemQuantityRequestSchema>;

/** Manzanita.Web.Models.ManzanitaSortedImage */
export const productImageSchema = z.object({
	largeImageUrl: z.string(),
	smallImageUrl: z.string(),
	sortOrder: z.number(),
});

/** Manzanita.Web.Models.CustomerOrderProductModel */
const customerProductSchema = z.object({
	product: productModelSchema,
	quantity: z.number(),
	quantityLimit: z.number().nullable(),
	unitCost: z.string(),
	totalLineCost: z.string(),
	requiresApproval: z.boolean(),
	showInventoryStatus: z.boolean(),
	estimatedInStockDate: localDateTimeSchema.nullable().or(z.string().nullable()),
});

/** AppleRetail.Web.Models.ShippingOptionModel */
const shippingMethodSchema = z.object({
	displayName: z.string(),
	id: z.number(),
	price: z.string(),
});

/** AppleRetail.Web.Features.Customers.Models.CustomerModel */
const customerSchema = z.object({
	id: z.string(),
	name: z.string(),
	customerCode: z.string(),
	streetAddress1: z.string(),
	streetAddress2: z.string(),
	streetAddress3: z.string(),
	city: z.string(),
	stateOrProvince: z.string(),
	postalCode: z.string(),
	countryCode: z.string(),
	countryCodes: z.array(z.string()),
	thumbnailUrl: z.string().nullable(),
	storeNumber: z.string(),
	isDeleted: z.boolean(),
	customerGroup: customerGroupSchema,
	customerClassification: z.string(),
	logoImageUrl: z.string(),
	cityStatePostalDisplay: z.string(),
	countryNameDisplay: z.string(),
	thirdPartyCustomerId: z.string(),
	vintage: z.string().nullable(),
});

/** Manzanita.Web.Models.CustomerOrderModel */
const customerOrderSchema = z.object({
	customer: customerSchema,
	selectedShippingOptionId: z.number(),
	subTotalCost: z.string(),
	shippingCost: z.string().nullable(),
	totalCost: z.string(),
	isCostOverBudget: z.boolean(),
	estimatedShipDate: z.string().nullable(),
	availableShippingMethods: z.array(shippingMethodSchema),
	customerProducts: z.array(customerProductSchema),
	totalQuantity: z.number(),
	formattedTotalOverBudget: z.string().nullable(),
});

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.MultipleCustomerOrderResponse */
export const storeCartsResponseSchema = z.object({
	customerOrders: z.array(customerOrderSchema),
	nonZeroQuantityItemCount: z.number(),
	orderRequiresApproval: z.boolean().optional(),
});

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.OrderItemQuantityUpdateResponse */
export const storeCartResponseSchema = z.object({
	customerOrder: customerOrderSchema,
	nonZeroQuantityItemCount: z.number(),
});

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.CustomerOrderFilter */
export const storeCartLineFiltersSchema = z.object({
	salesOrderNumber: z.string().optional(),
	excludeZeroQuantityLineItems: z.boolean().optional(),
});

/** AppleRetail.Web.ApiControllers.OrderItemQuantityApiController.UpdateLineItemQuantityRequest */
export const setStoreItemQuantityRequestSchema = z.object({
	customerCode: z.string(),
	itemId: productKeySchema,
	quantity: z.number(),
});
